import { c as _c } from "react/compiler-runtime";
import { MaterialIcon } from "@website/icons/material";
import { useTable } from "@website/ui/datepicker/components/table/table.hook";
import styles from "@website/ui/datepicker/components/table/table.module.scss";
import { TablePropertiesType } from "@website/ui/datepicker/components/table/table.types";
import { hasDiscountInRow } from "@website/ui/datepicker/components/table/table.utils";
import { MONTH_NAMES, WEEK_ABBR } from "@website/ui/datepicker/constanst";
import { DatepickerDayStateType } from "@website/ui/datepicker/datepicker.types";
import { getNativeGregorianMonthName } from "@website/ui/datepicker/datepicker.utils";
import { Tooltip } from "@website/ui/tooltip/tooltip";
import { numberConvert } from "@website/utils/number-convert";
import { FC } from "react";
const RANGE_SELECTION_STATES: ReadonlySet<DatepickerDayStateType> = new Set(["selected", "multi-selected", "start", "in-range", "middle", "end"]);
export const Table: FC<TablePropertiesType> = properties => {
  const $ = _c(30);
  const {
    calendarLocale,
    year,
    month,
    type,
    view,
    isLabelTop
  } = properties;
  const {
    days
  } = useTable(properties);
  const isFlat = type === "flat";
  const monthName = (MONTH_NAMES[calendarLocale] || MONTH_NAMES.gregorian)[month - 1];
  let t0;
  if ($[0] !== year) {
    t0 = numberConvert(year, {
      numberOnly: true,
      locale: "fa"
    });
    $[0] = year;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const localizedYear = t0;
  const t1 = `${styles.container} ${isFlat ? styles["container-flat"] : ""} ${styles["rtl" || "ltr"]}`;
  const t2 = `${isFlat && view !== "mobile" ? styles["week-names-container-desktop-flat"] : !isFlat && view !== "mobile" ? `${styles["week-names-container-desktop"]} ${styles["week-names-container-desktop__default"]}` : styles["week-names-container-desktop"]} `;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [1, 2, 3, 4, 5, 6, 7];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== calendarLocale || $[4] !== isFlat) {
    t4 = t3.map(week => <div className={styles["week-name"]} key={week}><div className={isFlat ? styles["aspect-ratio-content"] : ""}>{WEEK_ABBR[calendarLocale][week - 1]}</div></div>);
    $[3] = calendarLocale;
    $[4] = isFlat;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `${styles["month-name"]} ${view === "mobile" ? "" : styles["month-name__default"]} ${isLabelTop ? `absolute inset-x-0 top-0 z-[-1] !mx-0 !mt-4 truncate px-16 ${styles["month-name-label-top"]}` : ""}`;
  let t7;
  if ($[9] !== calendarLocale || $[10] !== month || $[11] !== year) {
    t7 = calendarLocale === "gregorian" ? <span className={styles["month-name-native"]} dir="ltr">({getNativeGregorianMonthName(year, month)})</span> : null;
    $[9] = calendarLocale;
    $[10] = month;
    $[11] = year;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== localizedYear || $[14] !== monthName || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className={t6}>{monthName} {localizedYear}{t7}</div>;
    $[13] = localizedYear;
    $[14] = monthName;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [0, 1, 2, 3, 4, 5];
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== days || $[20] !== isFlat || $[21] !== localizedYear || $[22] !== monthName || $[23] !== view) {
    t10 = t9.map(row => {
      if (days[row * 7]?.value !== -1 || days[row * 7 + 6]?.value !== -1) {
        return <div className={`${styles.row} ${isFlat ? "" : view === "mobile" ? styles.row__mobile : styles.row__default}`} key={row}>{[0, 1, 2, 3, 4, 5, 6].map((cell, cellIndex) => {
            const index = row * 7 + cellIndex;
            const cellText = days[index]?.value === -1 ? "" : days[index]?.value;
            const localizedCellText = numberConvert(cellText, {
              numberOnly: true,
              locale: "fa"
            });
            const cellContent = days[index]?.content;
            const hasContent = Boolean(cellContent);
            const state = days[index]?.state;
            const alignWithDiscountedRow = hasDiscountInRow(days, index);
            const isToday = days[index]?.isToday;
            const nonInstantAvailability = days[index]?.nonInstantAvailability;
            const availability = days[index]?.availability;
            const hasDiscount = days[index]?.hasDiscount;
            const isSoldOut = days[index]?.isSoldOut;
            const tooltip = days[index]?.tooltip;
            const isSelected = RANGE_SELECTION_STATES.has(state);
            const accessibleContent = typeof cellContent === "string" || typeof cellContent === "number" ? String(cellContent) : "";
            const dateAriaLabel = cellText ? `${localizedCellText} ${monthName} ${localizedYear}${accessibleContent ? `، ${accessibleContent}` : ""}` : undefined;
            return <button aria-current={isToday ? "date" : undefined} aria-label={dateAriaLabel} aria-pressed={cellText ? isSelected : undefined} className={`${styles.cell} ${isFlat ? styles.cell__flat : view === "mobile" ? styles.cell__mobile : styles.cell__default} ${isToday ? styles.today : ""} ${hasDiscount ? styles.discounted : ""} ${alignWithDiscountedRow ? styles["align-with-discount"] : ""} ${isSoldOut ? styles["sold-out"] : ""} ${state ? styles[state] : ""}`} disabled={!days[index]?.onClick} key={cell} onClick={days[index]?.onClick} type="button"><div className={`${styles["cell-inside"]} ${isFlat ? styles["cell-inside__flat"] : view === "mobile" ? styles["cell-inside__mobile"] : styles["cell-inside__default"]}`}><Tooltip className="!h-max !w-full" placement={row === 0 ? "bottom" : "top"} title={tooltip} titleClassName={`!left-0 ${!tooltip && "hidden"}`}>{nonInstantAvailability === 0 && availability > 0 && <MaterialIcon className="text-warning absolute left-0 top-0 !h-3 !w-3 text-xs leading-3" name="bolt" />}<div className={styles["cell-inside-date"]}>{localizedCellText}</div>{hasContent && <span className={styles["cell-inside-data"]}>{cellContent}</span>}</Tooltip></div></button>;
          })}</div>;
      }
      return null;
    });
    $[19] = days;
    $[20] = isFlat;
    $[21] = localizedYear;
    $[22] = monthName;
    $[23] = view;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t1 || $[26] !== t10 || $[27] !== t5 || $[28] !== t8) {
    t11 = <div className={t1}>{t5}{t8}{t10}</div>;
    $[25] = t1;
    $[26] = t10;
    $[27] = t5;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};